import * as React from "react";

import { FleetTenant, FleetTenantHistory, FleetTenantStatus } from "api/fleet";
import { isConnected } from "Components/Tenants";
import { useAuth } from "hooks/useAuth";
import { useFleetHistory, useFleetOverview } from "hooks/useFleet";
import { useBillingOverviewQuery } from "Routes/Billing/data";

/** A tenant from the live overview plus its 24 h uptime and latency from the history. */
export interface DashboardTenantRow extends FleetTenant {
  history: FleetTenantHistory | null;
}

/** The tenant board's two groups: connected to SAP, and greyed until their TeleOpd connects. */
export interface DashboardTenantGroups {
  connected: DashboardTenantRow[];
  notConnected: DashboardTenantRow[];
}

// Problems first, so each group reads as a to-do list.
const STATUS_ORDER: Record<FleetTenantStatus, number> = { down: 0, degraded: 1, unknown: 2, up: 3 };

/** Everything the dashboard shows comes from these three live queries; nothing is invented. */
export const useDashboardData = () => {
  const { hasPermission } = useAuth();
  const canSeeFleet = hasPermission("tenantManagement", "read");
  const canSeeBilling = hasPermission("billing", "read");

  const overview = useFleetOverview(canSeeFleet);
  const history = useFleetHistory(24, canSeeFleet);
  const billing = useBillingOverviewQuery(canSeeBilling);

  const tenants = overview.data?.tenants;
  const tenantHistory = history.data?.tenants;
  const rows = React.useMemo<DashboardTenantRow[]>(() => {
    const historyById = new Map((tenantHistory || []).map((entry) => [entry.tenantId, entry]));
    return (tenants || [])
      .map((tenant) => ({ ...tenant, history: historyById.get(tenant.tenantId) || null }))
      .sort(
        (a, b) =>
          STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
          a.name.localeCompare(b.name) ||
          a.slug.localeCompare(b.slug)
      );
  }, [tenants, tenantHistory]);

  const groups = React.useMemo<DashboardTenantGroups>(
    () => ({
      connected: rows.filter((row) => isConnected(row.link)),
      notConnected: rows.filter((row) => !isConnected(row.link)),
    }),
    [rows]
  );

  // When the newest health check ran (probes are cached for a minute on the server).
  const lastCheckedAt = React.useMemo<string | null>(
    () =>
      rows.reduce<string | null>((latest, row) => {
        const at = row.probe?.checkedAt;
        return at && (!latest || at > latest) ? at : latest;
      }, null),
    [rows]
  );

  const { refetch: refetchOverview } = overview;
  const { refetch: refetchHistory } = history;
  const { refetch: refetchBilling } = billing;
  const refresh = React.useCallback(() => {
    if (canSeeFleet) {
      refetchOverview();
      refetchHistory();
    }
    if (canSeeBilling) {
      refetchBilling();
    }
  }, [canSeeBilling, canSeeFleet, refetchBilling, refetchHistory, refetchOverview]);

  return {
    canSeeFleet,
    canSeeBilling,
    overview,
    history,
    billing,
    rows,
    groups,
    lastCheckedAt,
    refresh,
    refreshing: overview.isFetching || history.isFetching || billing.isFetching,
  };
};
